import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup, ValidationErrors } from '@angular/forms';
import { DetailGroupInfo } from 'common/model';
import { WizardComponent } from "@app/external/angular-archwizard";
import { BsModalService } from 'ngx-bootstrap/modal';
import { BehaviorSubject, Subscription } from 'rxjs';
import { applyFieldModeToFormGroup, applyVariantVisibilityToFormGroup, DetailInfoFormStore, generateFormGroupForInputContentGroup } from '../shared.helper';
import { FormVariantOption } from 'shared/form-variant-selector/form-variant-selector.component';
import { VariantContextService } from 'shared/variant-context.service';

export type DetailInfoPageData = {
  detailGroupInfoListsArray: DetailGroupInfo[][][], formGroupLists: FormGroup[][]
};

@Component({
  selector: 'detail-info-form-page-steps',
  templateUrl: './detail-info-form-page-steps.component.html',
  styleUrls: ['./detail-info-form-page-steps.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
  providers: [VariantContextService]
})

export class DetailInfoFormPageStepsComponent implements OnChanges {

  @Input() detailGroupInfoList: DetailGroupInfo[] | DetailGroupInfo[][] = [];
  @Input() processing$ = new BehaviorSubject<boolean>(false);
  @Input() showSaveAndNew = false;
  @Output() formSubmit = new EventEmitter<any>();
  @Output() formSubmitAndNew = new EventEmitter<any>();
  @Output() afterSave = new EventEmitter<object>();
  @Input() update = false;
  @Input() readOnly = false;
  // Disable all fields like readOnly, but KEEP the Save/actions row. Used when a clinical note is
  // locked yet the surrounding attendance still needs to be edited and saved from the same form.
  @Input() fieldsReadOnly = false;
  @Input() showReset = true;
  @Input() showCancel = true;
  @Input() cancelText = 'Cancel';
  @Input() multipleForms = false;
  @Input() currentFormIndex: number = 0;
  @Output() currentFormIndexChange = new EventEmitter<number>();

  @Input() variants: FormVariantOption[] = [];
  @Input() selectedVariantGuid: string | null = null;
  /** True when the variant came from a Program/Activity pin - selector renders read-only. */
  @Input() variantPinned = false;
  @Input() formGuid: string | null = null;
  @Output() variantChange = new EventEmitter<string | null>();

  /** Referral workflow: field guids to hide (value cleared) / force-require for the current
   *  action, applied on top of variant visibility. Empty = no effect. */
  @Input() hiddenFieldGuids: string[] = [];
  @Input() requiredFieldGuids: string[] = [];
  /** Therapy approval: fields shown but locked (value still posted). */
  @Input() readOnlyFieldGuids: string[] = [];
  /** Label for the final Save button (e.g. "Request Therapy"). */
  @Input() saveText = 'Save';

  /** True while the form has variants defined but the user hasn't picked one yet
   *  (and no default auto-selected). Wizard content is hidden until selection lands. */
  public get awaitingVariantSelection(): boolean {
    return (this.variants?.length ?? 0) > 0 && !this.selectedVariantGuid;
  }

  public detailGroupInfoListArray: DetailGroupInfo[][] = [];

  public pageData!: DetailInfoPageData;
  private subs = new Subscription();
  isSubmitted: boolean = false;

  public errors: string[] = [];

  @ViewChild(WizardComponent) public wizard: WizardComponent;
  get currentStepIndex(): number {
    return [this.wizard?.currentStepIndex, 0].includes(-1) ? 0 : (this.wizard?.currentStepIndex ?? 0);
  }

  constructor(
    private fb: FormBuilder, private modalService: BsModalService,
    private variantContext: VariantContextService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit() {
    if (!this.multipleForms) {
      this.detailGroupInfoListArray = [this.detailGroupInfoList as DetailGroupInfo[]];
    } else {
      this.detailGroupInfoListArray = this.detailGroupInfoList as DetailGroupInfo[][];
    }
    this.pageData = this.generatePageForms(this.detailGroupInfoListArray);
    this.variantContext.setSelectedVariantGuid(this.selectedVariantGuid);
    this.applyVariantVisibilityToAllForms(this.selectedVariantGuid);
    // Read-only must be applied AFTER variant visibility: applyVariantVisibilityToAllForms
    // re-enables every visible control, which previously undid the disable and left
    // "view" modals fully editable.
    this.applyReadOnlyState();

    DetailInfoFormStore.submitted$.next(false);
    const sub = DetailInfoFormStore.reset$.subscribe(() => this.wizard.reset());
    this.subs.add(sub);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedVariantGuid'] && !changes['selectedVariantGuid'].firstChange && this.pageData) {
      this.applyVariantVisibilityToAllForms(this.selectedVariantGuid);
    }
    // The action (refer / accept) can change after the form is built — re-apply from the
    // definition so a field hidden by the previous action comes back.
    const fieldMode = changes['hiddenFieldGuids'] ?? changes['requiredFieldGuids'] ?? changes['readOnlyFieldGuids'];
    if (fieldMode && !fieldMode.firstChange && this.pageData) {
      this.applyVariantVisibilityToAllForms(this.selectedVariantGuid);
      this.applyReadOnlyState();
    }
    // readOnly / fieldsReadOnly can arrive AFTER the form is built (e.g. a clinical-note lock flag
    // fetched on a separate call), so re-assert the disabled state whenever they change.
    if ((changes['readOnly'] || changes['fieldsReadOnly']) && this.pageData) {
      this.applyReadOnlyState();
    }
  }


  public onVariantSelectionChange(variantGuid: string | null): void {
    this.selectedVariantGuid = variantGuid;
    this.variantContext.setSelectedVariantGuid(variantGuid);
    this.applyVariantVisibilityToAllForms(variantGuid);
    // Re-applying visibility re-enables visible controls, so re-assert read-only after it.
    this.applyReadOnlyState();
    this.variantChange.emit(variantGuid);
    this.cdr.markForCheck();
  }

  /** Disable every form when in read-only mode. Must run AFTER variant-visibility, which
   *  re-enables visible controls. No-op when not read-only, so edit/add forms are untouched. */
  private applyReadOnlyState(): void {
    if (!(this.readOnly || this.fieldsReadOnly) || !this.pageData) return;
    this.pageData.formGroupLists.forEach(formGroupList =>
      formGroupList.forEach(form => form.disable({ emitEvent: false }))
    );
  }

  private applyVariantVisibilityToAllForms(variantGuid: string | null): void {
    if (!this.pageData) return;
    this.pageData.formGroupLists.forEach((formGroupList, formIndex) => {
      formGroupList.forEach((form, groupIndex) => {
        const detailGroupInfos = this.pageData.detailGroupInfoListsArray[formIndex]?.[groupIndex];
        if (detailGroupInfos) {
          applyVariantVisibilityToFormGroup(form, detailGroupInfos, variantGuid);
          applyFieldModeToFormGroup(form, detailGroupInfos, this.hiddenFieldGuids, this.requiredFieldGuids, this.readOnlyFieldGuids);
        }
      });
    });
  }

  private generatePageForms(detailGroupInfoList: DetailGroupInfo[][]): DetailInfoPageData {
    const pageData: DetailInfoPageData = { formGroupLists: [], detailGroupInfoListsArray: [] };

    detailGroupInfoList.forEach((detailGroupInfo, detailInfoIndex) => {
      let currentForm = this.fb.group({});
      let currentGroup: DetailGroupInfo[] = [];
      pageData.formGroupLists[detailInfoIndex] = [];
      pageData.detailGroupInfoListsArray[detailInfoIndex] = [];
      detailGroupInfo.forEach((group, index) => {
        if (index === 0 || group.pageBreak) {
          currentForm = this.fb.group({});
          currentGroup = [];
          pageData.formGroupLists[detailInfoIndex].push(currentForm);
          pageData.detailGroupInfoListsArray[detailInfoIndex].push(currentGroup);
        }
        currentForm.addControl(group.uuid, generateFormGroupForInputContentGroup(this.fb, group));
        currentGroup.push(group);
      });
      if (!pageData.formGroupLists[detailInfoIndex].length) {
        pageData.formGroupLists[detailInfoIndex].push(currentForm);
      }
    })

    return pageData;
  }

  public submit(saveAndNew = false) {
    this.isSubmitted = true;
    this.pageData.formGroupLists.forEach(formGroupList => {
      formGroupList.forEach(form => {
        form.markAllAsTouched();
        this.markFormGroupDirty(form);
      })
    });
    DetailInfoFormStore.touched$.next();
    DetailInfoFormStore.submitted$.next(true);

    const invalidForm = this.pageData.formGroupLists.some(formGroupList =>
      formGroupList.some(form => form.invalid)
    );
    if (invalidForm) {
      console.warn('Invalid form ', this.pageData.formGroupLists);
      this.errors = [];
      this.pageData.formGroupLists.forEach(formGroupList => {
        formGroupList.forEach(form => {
          for (const formGroupKey in form.controls) {
            const formGroup = form.controls[formGroupKey] as FormGroup;
            for (const formControlKey in formGroup.controls) {
              if (formGroup.controls[formControlKey].hasError) {
                if (formGroup.controls[formControlKey].errors !== null) {
                  this.errors.push(Object.keys(formGroup.controls[formControlKey].errors).join(','))
                }
              }
            }
          }
        })
      });

      this.formSubmit.emit(false);
      return;
    }
    const rawValues = [];
    this.pageData.formGroupLists.forEach((formGroupList, index) => {
      rawValues[index] = {};
      formGroupList.forEach(form => Object.assign(rawValues[index], form.getRawValue()))
    });

    const payload = this.multipleForms ? rawValues : rawValues[0];
    (saveAndNew ? this.formSubmitAndNew : this.formSubmit).emit(payload);
  }

  public reset() {
    this.pageData.formGroupLists.forEach(formGroupList => {
      formGroupList.forEach(x => {
        x.reset();
      });
    });

  }

  next() {
    this.isSubmitted = true;
  }

  back() {
    this.isSubmitted = true;
  }

  nextForm() {
    this.currentFormIndex++;
    this.currentFormIndexChange.emit(this.currentFormIndex);
  }

  previousForm() {
    this.currentFormIndex--;
    this.currentFormIndexChange.emit(this.currentFormIndex);
  }

  // MUST be bound as [canExit]="canExit.bind(this)" (a function, evaluated only when the
  // wizard actually navigates) — NEVER as [canExit]="canExit()". The call form runs this on
  // EVERY change-detection pass, and the touched$.next() below then re-triggers change
  // detection through any subscribing field component (detail-info-select-with-text queues a
  // microtask per emission): after an invalid submit, tick → emit → microtask → tick looped
  // inside a single task and hard-froze the page (Chrome RESULT_CODE_HUNG) on any form
  // containing a dropdown-with-comment field.
  canExit() {
    const form = this.pageData?.formGroupLists?.[this.currentFormIndex]?.[this.currentStepIndex];
    if (!form) {
      return true;
    }
    if (this.isSubmitted) {
      form.markAllAsTouched();
      this.markFormGroupDirty(form);
      DetailInfoFormStore.touched$.next();
    }
    if (!form.invalid) {
      this.isSubmitted = false;
    }
    return !form.invalid;
  }

  private markFormGroupDirty(form: FormGroup) {
    Object.keys(form.controls).forEach((key) => {
      const control = form.controls[key];
      if (control instanceof FormGroup) {
        this.markFormGroupDirty(control);
      } else {
        control.markAsDirty({ onlySelf: true });
      }
    });
  }

  closeDialog(addEditSuccess): void {
    this.afterSave.emit(addEditSuccess);
    this.modalService.setDismissReason('cancel');
    this.modalService.hide();
  }
}